import { faPlay, faUpload, faUndo, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createKeyPath } from 'CMFW/context/helper/keyContext';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import { Howl } from 'howler';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IAudioDetails } from 'types/audio';
import AudioManagementModal from 'layout/modal/AudioManagementModal';

const AUDIO_KEY = 'audio';

type IProps = {
  className?: string;
  // Optional audio provided via props
  audio?: IAudioDetails;
  onAudioChange?: (audio: IAudioDetails) => void;
};

enum IPlaybackState {
  PAUSED,
  PLAYING,
  ERROR,
  NO_AUDIO,
}

const getColor = (state: IPlaybackState, isInActiveSet = false) => {
  switch (state) {
    case IPlaybackState.PAUSED:
      return 'black';
    case IPlaybackState.PLAYING:
      return 'green';
    case IPlaybackState.ERROR:
      return 'tomato';
    case IPlaybackState.NO_AUDIO:
      return isInActiveSet ? '#ff9800' : 'tomato'; // Orange only when no audio + active set
  }
};

export const AudioPlayer: React.FC<IProps> = (props) => {
  const [state, setState] = useState(IPlaybackState.PAUSED);
  const [showModal, setShowModal] = useState(false);

  const parentKey = useContext(KeyContext);
  const audioKey = createKeyPath(parentKey, AUDIO_KEY);

  const module = useContext(ModuleContext);

  // Get audio data from module context
  const getAudioFromModule = useCallback(() => {
    // First try to get from props
    if (props.audio) return props.audio;

    // Then check if parent object has audio property (e.g., IVocDialect.audio)
    try {
      const parentValue = module.getItemUpdatedValue(parentKey);
      if (parentValue && typeof parentValue === 'object' && 'audio' in parentValue) {
        const parentAudio = (parentValue as any).audio;
        if (parentAudio) return parentAudio;
      }
    } catch (error) {
      // Continue to other methods if parent doesn't exist
    }

    // Try the dedicated audio key (prefer updated value)
    const moduleAudioUpdated = module.getItemUpdatedValue(audioKey);
    if (moduleAudioUpdated) return moduleAudioUpdated;
    const moduleAudio = module.getItemValue(audioKey);
    if (moduleAudio) return moduleAudio;

    return null;
  }, [module, audioKey, parentKey, props.audio]);

  // Use local state to manage audio data
  const [audioData, setAudioData] = useState<IAudioDetails | null>(getAudioFromModule());

  // Track the parent object to detect changes
  const moduleParent = module.getItemUpdatedValue(parentKey);

  // Sync with module context when data changes
  useEffect(() => {
    const newAudioData = getAudioFromModule();
    setAudioData(newAudioData);
  }, [props.audio, moduleParent, getAudioFromModule]);

  const audioPath = audioData?.path || '';
  const hasAudio = !!audioPath;
  const canRecover = audioData?.canRecover || false;
  const isInActiveSet = !!audioData?.audioSet?.active;

  useEffect(() => {
    if (!hasAudio) {
      setState(canRecover || !audioData ? IPlaybackState.NO_AUDIO : IPlaybackState.ERROR);
    } else {
      setState(IPlaybackState.PAUSED);
    }
  }, [hasAudio, canRecover, audioData]);

  const sound = useMemo(
    () =>
      hasAudio
        ? new Howl({
            src: [audioPath],
            preload: false,
          })
        : null,
    [audioPath, hasAudio],
  );

  useEffect(
    () => () => {
      setState(IPlaybackState.PAUSED);
      sound?.unload();
    },
    [sound],
  );

  if (sound) {
    sound.on('end', () => setState(IPlaybackState.PAUSED));
    sound.on('loaderror', () => setState(IPlaybackState.ERROR));
    sound.on('playerror', () => setState(IPlaybackState.ERROR));
  }

  const handlePlayPause = useCallback(() => {
    if (!hasAudio || !sound) return;

    switch (state) {
      case IPlaybackState.PAUSED:
        setState(IPlaybackState.PLAYING);
        sound.load();
        sound.play();
        break;

      case IPlaybackState.PLAYING:
        setState(IPlaybackState.PAUSED);
        sound.stop();
        break;

      default:
        break;
    }
  }, [state, sound, hasAudio]);

  const handleClick = useCallback(() => {
    console.log(audioData);
    console.log('hasAudio', hasAudio);
    // If we don't have proper audio data (ref, refId, dialectId), just play/pause
    if (!audioData || audioData.ref === 0) {
      handlePlayPause();
      return;
    }

    // Single click behavior
    if (!hasAudio) {
      setShowModal(true);
    } else {
      handlePlayPause();
    }
  }, [hasAudio, handlePlayPause, audioData]);

  const handleManagementClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent play/pause when clicking management icon
    setShowModal(true);
  }, []);

  const getIcon = () => {
    if (!hasAudio && canRecover) {
      return faUndo;
    } else if (!hasAudio) {
      return faUpload;
    }
    return faPlay;
  };

  const getTooltip = () => {
    if (!audioData || audioData.ref === 0) {
      return hasAudio ? 'Click to play' : 'No audio available';
    }

    if (isInActiveSet) {
      return `Audio belongs to active set: ${audioData.audioSet?.name}`;
    } else if (hasAudio) {
      return 'Click to play, hover for management';
    } else if (canRecover) {
      return 'Click to recover audio';
    } else {
      return 'Click to upload audio';
    }
  };

  return (
    <>
      <Container
        onClick={handleClick}
        $state={state}
        $hasAudio={hasAudio}
        $isInActiveSet={isInActiveSet}
        className={props.className}
        title={getTooltip()}
      >
        <Icon icon={getIcon()} $color={getColor(state, isInActiveSet)} />
        {isInActiveSet && <ActiveSetIndicator />}
        {audioData?.ref !== 0 && !isInActiveSet && (
          <ManagementIcon onClick={handleManagementClick} title="Manage audio">
            <FontAwesomeIcon icon={faCog} />
          </ManagementIcon>
        )}
      </Container>

      {showModal && audioData && audioData.ref !== 0 && (
        <AudioManagementModal
          audio={audioData}
          onClose={() => setShowModal(false)}
          onAudioChange={(updatedAudio) => {
            // Update local state immediately for responsive UI
            setAudioData(updatedAudio);

            // Update the parent vocabulary object (main data source)
            try {
              const parentValue = module.getItemUpdatedValue(parentKey);
              if (parentValue && typeof parentValue === 'object' && 'audio' in parentValue) {
                module.updateItemValue(parentKey, {
                  ...parentValue,
                  audio: updatedAudio,
                });
              }
            } catch (error) {
              // Fallback: update via dedicated audio key
              module.updateItemValue(audioKey, updatedAudio);
            }

            // Notify parent component if callback provided
            props.onAudioChange?.(updatedAudio);
          }}
        />
      )}
    </>
  );
};

const Container = styled.div<{ $state: IPlaybackState; $hasAudio: boolean; $isInActiveSet: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$state === IPlaybackState.ERROR && props.$hasAudio ? 'default' : 'pointer')};
  position: relative;
  opacity: ${(props) => (props.$isInActiveSet ? 0.7 : 1)};
`;

const Icon = styled(FontAwesomeIcon)<{ $color: string }>`
  color: ${(props) => props.$color};
  && {
    width: 12px;
    height: 12px;
  }
`;

const ActiveSetIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 6px;
  height: 6px;
  background-color: #ff9800;
  border-radius: 50%;
  border: 1px solid white;
`;

const ManagementIcon = styled.div`
  position: absolute;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.12s ease, transform 0.12s ease;
  opacity: 0.25;

  ${Container}:hover & {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-50%) scale(1.05);
  }

  svg {
    width: 10px;
    height: 10px;
    color: #666;
  }
`;
